import { DropdownMenu as RadixDropdownMenu } from 'radix-ui';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '@/utils/cn';

import { usePopoverContainer, type PopoverPortalTarget } from './popover-portal';

/**
 * Action menus: row actions in a table, the overflow menu on a record, the account
 * menu in the top bar.
 *
 * A menu inside a dialog needs `popoverPortal="dialog"` exactly like a picker does.
 * The failure looks different — the menu opens behind the dim overlay — but the cause
 * is the same.
 */

export const DropdownMenu = RadixDropdownMenu.Root;
export const DropdownMenuTrigger = RadixDropdownMenu.Trigger;
export const DropdownMenuGroup = RadixDropdownMenu.Group;
export const DropdownMenuSub = RadixDropdownMenu.Sub;

export const DropdownMenuContent = forwardRef<
  ElementRef<typeof RadixDropdownMenu.Content>,
  ComponentPropsWithoutRef<typeof RadixDropdownMenu.Content> & {
    popoverPortal?: PopoverPortalTarget;
  }
>(({ className, sideOffset = 4, popoverPortal = 'body', ...props }, ref) => {
  const { container } = usePopoverContainer(popoverPortal);

  return (
    <RadixDropdownMenu.Portal container={container}>
      <RadixDropdownMenu.Content
        ref={ref}
        sideOffset={sideOffset}
        collisionPadding={12}
        className={cn(
          'z-50 min-w-48 overflow-hidden rounded-lg border border-border bg-surface-raised p-1 shadow-e3',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          className,
        )}
        {...props}
      />
    </RadixDropdownMenu.Portal>
  );
});

DropdownMenuContent.displayName = 'DropdownMenuContent';

export const DropdownMenuItem = forwardRef<
  ElementRef<typeof RadixDropdownMenu.Item>,
  ComponentPropsWithoutRef<typeof RadixDropdownMenu.Item> & { destructive?: boolean }
>(({ className, destructive, ...props }, ref) => (
  <RadixDropdownMenu.Item
    ref={ref}
    className={cn(
      'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-body outline-none',
      'data-[highlighted]:bg-surface-sunken',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      destructive
        ? 'text-danger-fg data-[highlighted]:bg-danger-subtle'
        : 'text-fg',
      className,
    )}
    {...props}
  />
));

DropdownMenuItem.displayName = 'DropdownMenuItem';

export const DropdownMenuLabel = ({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof RadixDropdownMenu.Label>) => (
  <RadixDropdownMenu.Label
    className={cn('px-2 py-1.5 text-overline font-semibold text-fg-tertiary uppercase', className)}
    {...props}
  />
);

export const DropdownMenuSeparator = ({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof RadixDropdownMenu.Separator>) => (
  <RadixDropdownMenu.Separator
    className={cn('my-1 h-px bg-border', className)}
    {...props}
  />
);

export const DropdownMenuSubTrigger = forwardRef<
  ElementRef<typeof RadixDropdownMenu.SubTrigger>,
  ComponentPropsWithoutRef<typeof RadixDropdownMenu.SubTrigger>
>(({ className, ...props }, ref) => (
  <RadixDropdownMenu.SubTrigger
    ref={ref}
    className={cn(
      'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-body text-fg outline-none',
      'data-[highlighted]:bg-surface-sunken data-[state=open]:bg-surface-sunken',
      className,
    )}
    {...props}
  />
));

DropdownMenuSubTrigger.displayName = 'DropdownMenuSubTrigger';

export const DropdownMenuSubContent = forwardRef<
  ElementRef<typeof RadixDropdownMenu.SubContent>,
  ComponentPropsWithoutRef<typeof RadixDropdownMenu.SubContent> & {
    popoverPortal?: PopoverPortalTarget;
  }
>(({ className, popoverPortal = 'body', ...props }, ref) => {
  const { container } = usePopoverContainer(popoverPortal);

  return (
    <RadixDropdownMenu.Portal container={container}>
      <RadixDropdownMenu.SubContent
        ref={ref}
        className={cn(
          'z-50 min-w-44 overflow-hidden rounded-lg border border-border bg-surface-raised p-1 shadow-e3',
          className,
        )}
        {...props}
      />
    </RadixDropdownMenu.Portal>
  );
});

DropdownMenuSubContent.displayName = 'DropdownMenuSubContent';
